import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../api/client';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Tooltip from '@mui/material/Tooltip';
import AdminLoading from './AdminLoading';
import AdminEmptyState from './AdminEmptyState';
import { IoBookOutline } from 'react-icons/io5';

export default function AdminBlogPosts() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dlgOpen, setDlgOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    id: null,
    slug: '',
    title: '',
    excerpt: '',
    body_html: '',
    featured_image_url: '',
    author_name: 'Editor',
    category_label: '',
    published: false,
  });

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    return api('/api/admin/blog-posts')
      .then(setList)
      .catch((e) => setError(e.message || 'Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setForm({
      id: null,
      slug: '',
      title: '',
      excerpt: '',
      body_html: '<p></p>',
      featured_image_url: '',
      author_name: 'Editor',
      category_label: '',
      published: false,
    });
    setDlgOpen(true);
  };

  const openEdit = async (row) => {
    setError('');
    try {
      const p = await api(`/api/admin/blog-posts/${row.id}`);
      setForm({
        id: p.id,
        slug: p.slug || '',
        title: p.title || '',
        excerpt: p.excerpt || '',
        body_html: p.body_html || '',
        featured_image_url: p.featured_image_url || '',
        author_name: p.author_name || 'Editor',
        category_label: p.category_label || '',
        published: Boolean(Number(p.published)),
      });
      setDlgOpen(true);
    } catch (e) {
      setError(e.message || 'Failed to load');
    }
  };

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      if (!form.id) {
        await api('/api/admin/blog-posts', {
          method: 'POST',
          body: JSON.stringify({
            slug: form.slug.trim(),
            title: form.title.trim(),
            excerpt: form.excerpt.trim() || undefined,
            body_html: form.body_html,
            featured_image_url: form.featured_image_url.trim() || undefined,
            author_name: form.author_name.trim() || 'Editor',
            category_label: form.category_label.trim() || undefined,
            published: form.published ? 1 : 0,
          }),
        });
      } else {
        await api(`/api/admin/blog-posts/${form.id}`, {
          method: 'PUT',
          body: JSON.stringify({
            slug: form.slug.trim(),
            title: form.title.trim(),
            excerpt: form.excerpt || null,
            body_html: form.body_html,
            featured_image_url: form.featured_image_url.trim() || null,
            author_name: form.author_name.trim() || 'Editor',
            category_label: form.category_label.trim() || null,
            published: form.published ? 1 : 0,
          }),
        });
      }
      setDlgOpen(false);
      await load();
    } catch (e) {
      setError(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const del = async (row) => {
    if (!window.confirm(`Delete "${row.title}"?`)) return;
    try {
      await api(`/api/admin/blog-posts/${row.id}`, { method: 'DELETE' });
      await load();
    } catch (e) {
      setError(e.message || 'Delete failed');
    }
  };

  if (loading) return <AdminLoading />;

  return (
    <Card>
      <CardHeader
        title="Blog posts"
        subheader={
          <>
            Storefront:&nbsp;
            <Link to="/blog" target="_blank" rel="noopener noreferrer">
              /blog
            </Link>
            . Use HTML for body — upload images via Admin uploads or paste /uploads/… URLs.
          </>
        }
        action={
          <Button variant="contained" size="small" onClick={openCreate}>
            New post
          </Button>
        }
      />
      <CardContent>
        {error && (
          <Typography color="error" className="admin-error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}
        {list.length === 0 ? (
          <AdminEmptyState message="No posts yet." icon={IoBookOutline} />
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Slug</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Published</TableCell>
                  <TableCell>Updated</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {list.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>
                      <Typography variant="body2" component="code">
                        {row.slug}
                      </Typography>
                    </TableCell>
                    <TableCell>{row.title}</TableCell>
                    <TableCell>{row.category_label || '–'}</TableCell>
                    <TableCell>{row.published ? 'Yes' : 'Draft'}</TableCell>
                    <TableCell>
                      {row.updated_at ? new Date(row.updated_at).toLocaleDateString() : '–'}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="View on storefront (if published)">
                        <span>
                          {row.published ? (
                            <Button
                              size="small"
                              component="a"
                              href={`/blog/${encodeURIComponent(row.slug)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              View
                            </Button>
                          ) : (
                            <Button size="small" disabled>
                              View
                            </Button>
                          )}
                        </span>
                      </Tooltip>
                      <Button size="small" onClick={() => openEdit(row)}>
                        Edit
                      </Button>
                      <Button size="small" color="error" onClick={() => del(row)}>
                        Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>

      <Dialog open={dlgOpen} onClose={() => !saving && setDlgOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>{form.id ? 'Edit blog post' : 'New blog post'}</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="URL slug"
              value={form.slug}
              onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
              required
              helperText="Lowercase letters, numbers, hyphens only (normalized on save)"
              fullWidth
              size="small"
            />
            <TextField
              label="Title"
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              required
              fullWidth
              size="small"
            />
            <TextField
              label="Excerpt"
              value={form.excerpt}
              onChange={(e) => setForm((f) => ({ ...f, excerpt: e.target.value }))}
              multiline
              rows={2}
              fullWidth
              size="small"
            />
            <TextField
              label="Body (HTML)"
              value={form.body_html}
              onChange={(e) => setForm((f) => ({ ...f, body_html: e.target.value }))}
              multiline
              rows={12}
              fullWidth
              size="small"
              required
              inputProps={{ spellCheck: false }}
            />
            <TextField
              label="Featured image URL"
              value={form.featured_image_url}
              onChange={(e) => setForm((f) => ({ ...f, featured_image_url: e.target.value }))}
              fullWidth
              size="small"
            />
            <TextField label="Author" value={form.author_name} onChange={(e) => setForm((f) => ({ ...f, author_name: e.target.value }))} fullWidth size="small" />
            <TextField
              label="Category label"
              placeholder="e.g. FASHION"
              value={form.category_label}
              onChange={(e) => setForm((f) => ({ ...f, category_label: e.target.value }))}
              fullWidth
              size="small"
            />
            <FormControlLabel
              control={
                <Checkbox checked={form.published} onChange={(e) => setForm((f) => ({ ...f, published: e.target.checked }))} />
              }
              label="Published (visible on /blog)"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDlgOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={save} disabled={saving || !String(form.slug).trim() || !String(form.title).trim()}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
